import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper,
  Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import { DeleteForever, Mail, Phone, Refresh, Visibility } from "@mui/icons-material";
import api from "lib/api/api";
import AusbildungNavbar from "./AusbildungNavbar";

const formatDate = (value) => value ? new Date(value).toLocaleString() : "-";
const shortText = (value, max = 120) => {
  if (!value) return "-";
  return value.length > max ? `${value.slice(0, max).trim()}...` : value;
};

const AusbildungInterestsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ausbildung-management/interests");
      setItems(Array.isArray(data) ? data : []);
      setSelectedIds([]);
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || "Could not load interests", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.categoryId ? String(item.categoryId) : "uncategorized";
      const current = map.get(key) || { id: key, name: item.categoryName || "Uncategorized", count: 0 };
      current.count += 1;
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesCategory = categoryFilter === "all" || String(item.categoryId || "uncategorized") === categoryFilter;
      const haystack = `${item.name || ""} ${item.email || ""} ${item.phone || ""} ${item.categoryName || ""} ${item.message || ""}`.toLowerCase();
      return matchesCategory && (!needle || haystack.includes(needle));
    });
  }, [items, categoryFilter, query]);

  const filteredIds = useMemo(() => filtered.map((item) => item.id), [filtered]);
  const selectedInFilter = selectedIds.filter((id) => filteredIds.includes(id));
  const allFilteredSelected = filteredIds.length > 0 && selectedInFilter.length === filteredIds.length;
  const someFilteredSelected = selectedInFilter.length > 0 && !allFilteredSelected;

  const changeCategory = (event) => {
    setCategoryFilter(event.target.value);
    setSelectedIds([]);
  };

  const toggleOne = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  };

  const toggleAllFiltered = () => {
    setSelectedIds(allFilteredSelected ? [] : filteredIds);
  };

  const removeOne = async () => {
    setDeleting(true);
    try {
      await api.delete(`/ausbildung-management/interests/${deleteTarget.id}`);
      setItems((current) => current.filter((item) => item.id !== deleteTarget.id));
      setSelectedIds((current) => current.filter((id) => id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteText("");
      setNotice({ open: true, message: "Interest deleted", severity: "success" });
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || "Deletion failed", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const removeSelected = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/ausbildung-management/interests/bulk-delete", { ids: selectedInFilter });
      setItems((current) => current.filter((item) => !selectedInFilter.includes(item.id)));
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      setDeleteText("");
      setNotice({ open: true, message: data?.message || "Selected interests deleted", severity: "success" });
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || "Bulk deletion failed", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return <Box sx={{ bgcolor: "#f5f7f8", minHeight: "100vh" }}>
    <AusbildungNavbar />
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Ausbildung Interests</Typography>
          <Typography color="text.secondary">Review and clean up students who registered interest in Ausbildung categories.</Typography>
        </Box>
        <Tooltip title="Refresh interests">
          <IconButton onClick={load} disabled={loading}><Refresh /></IconButton>
        </Tooltip>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ my: 3 }} alignItems={{ xs: "stretch", md: "center" }}>
        <TextField size="small" label="Search name, email, phone or message" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: { md: 340 } }} />
        <TextField select size="small" label="Category" value={categoryFilter} onChange={changeCategory} sx={{ minWidth: 240 }}>
          <MenuItem value="all">All categories ({items.length})</MenuItem>
          {categories.map((category) => <MenuItem key={category.id} value={category.id}>{category.name} ({category.count})</MenuItem>)}
        </TextField>
        <Button color="inherit" onClick={() => { setQuery(""); setCategoryFilter("all"); setSelectedIds([]); }}>Reset</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="error"
          variant="contained"
          startIcon={<DeleteForever />}
          disabled={!selectedInFilter.length || deleting}
          onClick={() => { setDeleteText(""); setBulkDeleteOpen(true); }}
        >
          Delete selected ({selectedInFilter.length})
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox checked={allFilteredSelected} indeterminate={someFilteredSelected} onChange={toggleAllFiltered} disabled={!filteredIds.length || loading} />
              </TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Notify</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={28} /></TableCell></TableRow> : filtered.length ? filtered.map((item) =>
              <TableRow key={item.id} hover selected={selectedIds.includes(item.id)}>
                <TableCell padding="checkbox"><Checkbox checked={selectedIds.includes(item.id)} onChange={() => toggleOne(item.id)} /></TableCell>
                <TableCell><strong>{item.name || "-"}</strong><br /><small>{item.interestType || "AUSBILDUNG"}</small></TableCell>
                <TableCell>{item.categoryName || "Uncategorized"}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {item.email && <Button component="a" href={`mailto:${item.email}`} size="small" startIcon={<Mail />} sx={{ justifyContent: "flex-start", p: 0 }}>{item.email}</Button>}
                    {item.phone && <Button component="a" href={`tel:${item.phone}`} size="small" startIcon={<Phone />} sx={{ justifyContent: "flex-start", p: 0 }}>{item.phone}</Button>}
                  </Stack>
                </TableCell>
                <TableCell><Chip size="small" label={item.notifyWhenAvailable ? "Yes" : "No"} color={item.notifyWhenAvailable ? "success" : "default"} /></TableCell>
                <TableCell sx={{ maxWidth: 340 }}>{shortText(item.message)}</TableCell>
                <TableCell>{formatDate(item.createdAt)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View message"><span><IconButton disabled={!item.message} onClick={() => setMessageTarget(item)}><Visibility /></IconButton></span></Tooltip>
                  <Tooltip title="Delete interest"><IconButton color="error" onClick={() => { setDeleteText(""); setDeleteTarget(item); }}><DeleteForever /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ) : <TableRow><TableCell colSpan={8} align="center">No interests found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>

    <Dialog open={Boolean(messageTarget)} onClose={() => setMessageTarget(null)} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={900}>{messageTarget?.name || "Student message"}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>{messageTarget?.categoryName || "Uncategorized"}</Alert>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>{messageTarget?.message || "-"}</Typography>
      </DialogContent>
      <DialogActions><Button onClick={() => setMessageTarget(null)}>Close</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="sm" fullWidth>
      <DialogTitle color="error" fontWeight={900}>Permanently delete interest?</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>This permanently deletes the selected interest record.</Alert>
        <Typography sx={{ mb: 2 }}>{deleteTarget?.name || "-"} - {deleteTarget?.email || "No email"}</Typography>
        <TextField fullWidth label="Type DELETE to confirm" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
        <Button color="error" variant="contained" disabled={deleteText !== "DELETE" || deleting} onClick={removeOne}>{deleting ? "Deleting..." : "Delete permanently"}</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={bulkDeleteOpen} onClose={() => !deleting && setBulkDeleteOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle color="error" fontWeight={900}>Delete selected interests?</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>This permanently deletes {selectedInFilter.length} interest record(s) from the current filtered list.</Alert>
        <TextField fullWidth label="Type DELETE to confirm" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBulkDeleteOpen(false)} disabled={deleting}>Cancel</Button>
        <Button color="error" variant="contained" disabled={deleteText !== "DELETE" || !selectedInFilter.length || deleting} onClick={removeSelected}>{deleting ? "Deleting..." : "Delete permanently"}</Button>
      </DialogActions>
    </Dialog>

    <Snackbar open={notice.open} autoHideDuration={5000} onClose={() => setNotice((value) => ({ ...value, open: false }))}>
      <Alert severity={notice.severity}>{notice.message}</Alert>
    </Snackbar>
  </Box>;
};

export default AusbildungInterestsPage;
